import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { toast } from "react-toastify";
import MediaBrowser from "./MediaBrowser";
import { updateMedia } from "../../api/mediaApi";
import "./media.scss";

/**
 * Reusable "choose a file" dialog backed by the school's media library.
 *
 * This is what makes the library worth having: any feature that needs a file
 * (the school logo, website blocks, announcement banners) opens this instead of
 * growing its own upload widget, so the same asset can be picked twice.
 *
 * Props:
 *   visible, onHide
 *   onSelect(item)  — called with the chosen asset
 *   typeLock        — restrict to a type, e.g. "image"
 *   publicUse       — the picked file will be shown to anonymous visitors (a
 *                     logo, a website image), so promote it to "public"
 *                     visibility on the way out — otherwise the file guard
 *                     would 404 it for everyone who isn't signed in
 *   header          — dialog title
 */
const MediaPickerDialog = ({
  visible,
  onHide,
  onSelect,
  typeLock = null,
  publicUse = false,
  header = "Choose from Media Library",
}) => {
  const [selected, setSelected] = useState(null);
  const [working, setWorking] = useState(false);

  const confirm = async (item) => {
    const chosen = item || selected;
    if (!chosen) return;

    let picked = chosen;
    if (publicUse && chosen.visibility !== "public") {
      setWorking(true);
      try {
        const res = await updateMedia(chosen.id, { visibility: "public" });
        picked = { ...chosen, ...res.item };
        // No toast — the dialog already states this up front in a Message.
      } catch (err) {
        // Hand back the file anyway — the admin can flip visibility in the
        // library — but don't let them think the website will render it.
        toast.warn(
          err.response?.data?.message ||
            "Could not make this file public — it may not display on your website"
        );
      } finally {
        setWorking(false);
      }
    }

    onSelect?.(picked);
    setSelected(null);
    onHide?.();
  };

  const close = () => {
    setSelected(null);
    onHide?.();
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" icon="pi pi-times" text onClick={close} />
      <Button
        label="Use this file"
        icon="pi pi-check"
        disabled={!selected}
        loading={working}
        onClick={() => confirm()}
      />
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={close}
      footer={footer}
      style={{ width: "min(60rem, 95vw)" }}
      breakpoints={{ "960px": "95vw" }}
      dismissableMask
    >
      {publicUse && (
        <Message
          severity="info"
          className="w-full mb-3 justify-content-start"
          text="This file will be shown on your public website, so it will be marked public — anyone with its link can open it."
        />
      )}
      <MediaBrowser
        selectedId={selected?.id}
        onSelect={setSelected}
        onPick={confirm}
        typeLock={typeLock}
        pageSize={18}
      />
      <small className="block mt-2 text-color-secondary">
        Tip: double-click a file to pick it straight away.
      </small>
    </Dialog>
  );
};

export default MediaPickerDialog;
